// features/workstations/dashboard-sections/entity-comparison.js
// ═══════════════════════════════════════════════════════════
// MULTI-ENTITY COMPARISON — C6 (Tier C)
// ═══════════════════════════════════════════════════════════
// Pick 2-4 entities (setups or tickers) from the user's trade
// history. Render side-by-side metric table. Headline calls out
// the leader + spread.
//
// V1 compares buckets from closed trades only. V2 could extend
// to companies (FMP profile) or any entity with a backing
// dataQuery function.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PractitionerLens,
} from '../../../components/widgets/primitives';

const KIND_OPTIONS = [
  { key: 'setup_type', label: 'by setup' },
  { key: 'ticker',     label: 'by ticker' },
];

const WINDOW_OPTIONS = [30, 60, 90, 180, 365];

const MAX_ENTITIES = 4;

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function EntityComparisonSection({ workstation, userId }) {
  const [kind, setKind] = useState('setup_type');
  const [windowDays, setWindowDays] = useState(90);
  const [universe, setUniverse] = useState([]);
  const [selected, setSelected] = useState([]);
  const [entities, setEntities] = useState([]);
  const [verdict, setVerdict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load the universe whenever kind/window change.
  const loadUniverse = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
        kind,
        days: String(windowDays),
      });
      const res = await fetch(`/api/workstations/entity-comparison?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setUniverse(payload?.universe || []);
      // Clear selections when kind/window changes since keys differ
      setSelected([]);
      setEntities([]);
      setVerdict(null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, kind, windowDays]);

  useEffect(() => { loadUniverse(); }, [loadUniverse]);

  // Load comparison details when selected entities change
  useEffect(() => {
    if (!workstation?.id || !userId) return;
    if (selected.length < 2) {
      setEntities([]);
      setVerdict(null);
      return;
    }
    const ac = new AbortController();
    (async () => {
      try {
        const qs = new URLSearchParams({
          workstation_id: workstation.id,
          user_id: userId,
          kind,
          days: String(windowDays),
          entities: selected.join(','),
        });
        const res = await fetch(`/api/workstations/entity-comparison?${qs}`, {
          cache: 'no-store',
          signal: ac.signal,
        });
        if (!res.ok) return;
        const payload = await res.json();
        setEntities(payload?.entities || []);
        setVerdict(payload?.verdict || null);
      } catch (_e) { /* aborted or network */ }
    })();
    return () => ac.abort();
  }, [selected, kind, windowDays, workstation?.id, userId]);

  const toggleSelect = (key) => {
    setSelected((prev) => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      if (prev.length >= MAX_ENTITIES) return prev;
      return [...prev, key];
    });
  };

  if (loading) {
    return (
      <section className="ws-compare">
        <div className="ws-compare-loading">Loading universe…</div>
      </section>
    );
  }

  return (
    <section className="ws-compare">
      <Eyebrow parts={[
        'multi-entity comparison',
        `${universe.length} available in ${windowDays}d`,
        selected.length > 0 ? `${selected.length} selected` : null,
      ]} />

      <SerifHeadline>{headlineFor(selected, verdict)}</SerifHeadline>
      <SerifSummary>{summaryFor(selected, verdict)}</SerifSummary>

      {/* Controls */}
      <div className="ws-compare-controls">
        <div className="ws-compare-kind-row">
          <span className="ws-compare-control-label">compare</span>
          {KIND_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`ws-compare-chip ${kind === opt.key ? 'is-active' : ''}`}
              onClick={() => setKind(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="ws-compare-window-row">
          <span className="ws-compare-control-label">window</span>
          {WINDOW_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              className={`ws-compare-chip ${d === windowDays ? 'is-active' : ''}`}
              onClick={() => setWindowDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Universe picker */}
      {universe.length === 0 ? (
        <div className="ws-compare-empty">
          No closed trades in the window to compare. Log more trades or widen the window.
        </div>
      ) : (
        <SectionStrip label={`pick 2–${MAX_ENTITIES} to compare`}>
          <div className="ws-compare-picker">
            {universe.map((u) => (
              <button
                key={u.key}
                type="button"
                className={`ws-compare-picker-chip ${selected.includes(u.key) ? 'is-selected' : ''}`}
                onClick={() => toggleSelect(u.key)}
                disabled={!selected.includes(u.key) && selected.length >= MAX_ENTITIES}
              >
                <span className="ws-compare-picker-key">{u.key}</span>
                <span className="ws-compare-picker-count">{u.count}</span>
              </button>
            ))}
          </div>
        </SectionStrip>
      )}

      {/* Comparison table */}
      {entities.length >= 2 && (
        <SectionStrip label="side-by-side" count={entities.length}>
          <ComparisonTable entities={entities} verdict={verdict} />
        </SectionStrip>
      )}

      {entities.length === 1 && (
        <div className="ws-compare-note">
          Pick at least one more to compare against.
        </div>
      )}

      {error && <div className="ws-compare-error">{error}</div>}

      <PractitionerLens label="how to use this">
        The leader+spread headline is the decision fuel: if one bucket
        meaningfully outperforms the others, the question becomes
        <em> why</em>, and whether the weak buckets deserve smaller
        size or should be dropped entirely. Small spreads mean the
        strategy generalizes — keep running the mix.
      </PractitionerLens>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// COMPARISON TABLE
// ═══════════════════════════════════════════════════════════
function ComparisonTable({ entities, verdict }) {
  const leaderKey = verdict?.leader;
  const laggardKey = verdict?.laggard;

  const metrics = [
    { key: 'sample_size',       label: 'count',        fmt: v => v == null ? '—' : v },
    { key: 'win_rate',          label: 'win rate',     fmt: v => v == null ? '—' : `${Math.round(v * 100)}%` },
    { key: 'avg_r',             label: 'avg R',        fmt: v => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`, primary: true },
    { key: 'total_r',           label: 'total R',      fmt: v => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}R` },
    { key: 'total_pnl',         label: 'total P&L',    fmt: v => v == null ? '—' : fmtUsdSigned(v) },
    { key: 'best_r',            label: 'best trade',   fmt: v => v == null ? '—' : `+${v.toFixed(2)}R` },
    { key: 'worst_r',           label: 'worst trade',  fmt: v => v == null ? '—' : `${v.toFixed(2)}R` },
    { key: 'max_drawdown_usd',  label: 'max dd',       fmt: v => v == null || v === 0 ? '—' : fmtUsdSigned(v) },
  ];

  return (
    <div className="ws-compare-table">
      <div
        className="ws-compare-table-row ws-compare-table-header"
        style={{ gridTemplateColumns: `140px repeat(${entities.length}, 1fr)` }}
      >
        <div>metric</div>
        {entities.map((e) => {
          const isLeader = e.key === leaderKey;
          const isLaggard = e.key === laggardKey && laggardKey !== leaderKey;
          const tone = isLeader ? 'sage' : isLaggard ? 'terra' : 'steel';
          return (
            <div key={e.key} className={`ws-compare-col-head ws-compare-col-head-${tone}`}>
              <span className="ws-compare-col-key">{e.key}</span>
              {isLeader && <span className="ws-compare-col-badge">leader</span>}
              {isLaggard && <span className="ws-compare-col-badge">laggard</span>}
            </div>
          );
        })}
      </div>
      {metrics.map((m) => (
        <div
          key={m.key}
          className={`ws-compare-table-row ${m.primary ? 'is-primary' : ''}`}
          style={{ gridTemplateColumns: `140px repeat(${entities.length}, 1fr)` }}
        >
          <div className="ws-compare-metric-label">{m.label}</div>
          {entities.map((e) => {
            const v = e[m.key];
            const tone = pickTone(m.key, v);
            return (
              <div
                key={e.key}
                className={`ws-compare-cell ws-compare-cell-${tone} ${m.primary ? 'is-primary' : ''}`}
              >
                {m.fmt(v)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ───
function pickTone(metricKey, value) {
  if (value == null) return 'steel';
  if (metricKey === 'avg_r' || metricKey === 'total_r' || metricKey === 'total_pnl' || metricKey === 'best_r') {
    return value > 0 ? 'sage' : value < 0 ? 'terra' : 'steel';
  }
  if (metricKey === 'worst_r') {
    return value >= -0.5 ? 'sage' : value >= -1 ? 'amber' : 'terra';
  }
  if (metricKey === 'max_drawdown_usd') {
    return value === 0 ? 'steel' : 'terra';
  }
  return 'steel';
}

function headlineFor(selected, verdict) {
  if (selected.length < 2) return 'Pick entities to compare.';
  if (verdict?.leader && verdict?.laggard && verdict?.spread_r != null) {
    return `${verdict.leader} leads ${verdict.laggard} by ${verdict.spread_r.toFixed(2)}R.`;
  }
  if (verdict?.leader) return `${verdict.leader} leads.`;
  return `${selected.length} selected — need more sample size for verdict.`;
}

function summaryFor(selected, verdict) {
  if (selected.length < 2) {
    return 'Pick 2–4 buckets from above. The widget computes count, win rate, avg R, best/worst, drawdown — and calls out the leader.';
  }
  if (verdict?.spread_r != null && verdict.spread_r >= 1.0) {
    return 'Significant gap. Consider whether the laggard deserves the same allocation as the leader, or whether it should be downsized / cut.';
  }
  if (verdict?.spread_r != null && verdict.spread_r >= 0.3) {
    return 'Moderate gap. Watch over more trades before restructuring allocation.';
  }
  return 'Pretty even — the strategy is generalizing across these buckets. Keep running the mix.';
}

function fmtUsdSigned(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '−' : n > 0 ? '+' : '';
  const abs = Math.abs(n);
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${Math.round(abs).toLocaleString()}`;
}
